import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import {
    Stack,
    TextField,
    IconButton,
    Text,
    mergeStyleSets,
    IStackTokens,
    Callout,
    DirectionalHint
} from '@fluentui/react';
import { DismissRegular } from '@fluentui/react-icons';

interface TagInputProps {
    label: string;
    value: string[];
    onChange: (tags: string[]) => void;
    placeholder?: string;
    description?: string;
    disabled?: boolean;
    maxTags?: number;
    suggestions?: string[];
}

const stackTokens: IStackTokens = { childrenGap: 8 };

const styles = mergeStyleSets({
    tagContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginTop: '8px',
        minHeight: '32px',
        alignItems: 'flex-start'
    },
    tag: {
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: '#f3f2f1',
        border: '1px solid #d2d0ce',
        borderRadius: '16px',
        padding: '4px 8px',
        fontSize: '12px',
        fontWeight: '400',
        color: '#323130',
        maxWidth: '300px',
        gap: '4px'
    },
    tagText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1
    },
    deleteButton: {
        minWidth: '16px',
        width: '16px',
        height: '16px',
        padding: '0',
        color: '#605e5c',
        ':hover': {
            backgroundColor: '#e1dfdd',
            color: '#323130'
        }
    },
    inputContainer: {
        marginBottom: '8px',
        position: 'relative' as const
    },
    suggestionsList: {
        maxHeight: '200px',
        overflowY: 'auto' as const,
        padding: '4px 0'
    },
    suggestionItem: {
        padding: '8px 12px',
        cursor: 'pointer',
        fontSize: '13px',
        ':hover': {
            backgroundColor: '#f3f2f1'
        }
    },
    suggestionItemSelected: {
        backgroundColor: '#deecf9'
    }
});

export const TagInput: React.FC<TagInputProps> = ({
    label,
    value,
    onChange,
    placeholder = "Type keywords and press Enter, or paste comma-separated values",
    description,
    disabled = false,
    maxTags,
    suggestions = []
}) => {
    // Ensure value is always an array
    const safeValue = Array.isArray(value) ? value : [];
    const [inputValue, setInputValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
    const inputRef = useRef<HTMLDivElement>(null);
    
    // Filter suggestions based on input and exclude already selected
    const filteredSuggestions = suggestions
        .filter(s => !safeValue.includes(s))
        .filter(s => inputValue.trim() === '' || s.toLowerCase().includes(inputValue.toLowerCase()))
        .slice(0, 10);  // Limit to 10 suggestions
    
    // Reset selection when suggestions change
    useEffect(() => {
        setSelectedSuggestionIndex(-1);
    }, [inputValue]);
    
    const addTag = (tag: string) => {
        const trimmedTag = tag.trim().replace(/^["']|["']$/g, '');
        if (trimmedTag && !safeValue.includes(trimmedTag)) {
            if (!maxTags || safeValue.length < maxTags) {
                onChange([...safeValue, trimmedTag]);
            }
        }
        setInputValue('');
        setShowSuggestions(false);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        // Handle arrow keys for suggestion navigation
        if (showSuggestions && filteredSuggestions.length > 0) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setSelectedSuggestionIndex(prev => 
                    prev < filteredSuggestions.length - 1 ? prev + 1 : 0
                );
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setSelectedSuggestionIndex(prev => 
                    prev > 0 ? prev - 1 : filteredSuggestions.length - 1
                );
                return;
            }
            if (event.key === 'Escape') {
                setShowSuggestions(false);
                return;
            }
        }
        
        if (event.key === 'Enter') {
            event.preventDefault();
            
            // If a suggestion is selected, use it
            if (selectedSuggestionIndex >= 0 && filteredSuggestions[selectedSuggestionIndex]) {
                addTag(filteredSuggestions[selectedSuggestionIndex]);
                return;
            }
            
            if (inputValue.trim()) {
                // Check if input contains commas for bulk entry
                if (inputValue.includes(',')) {
                    // Parse comma-separated values
                    const newTags = inputValue
                        .split(',')
                        .map(tag => tag.trim())
                        .map(tag => tag.replace(/^["']|["']$/g, '')) // Remove surrounding quotes
                        .filter(tag => tag.length > 0)
                        .filter(tag => !safeValue.includes(tag)); // Remove duplicates
                    
                    // Check max tags limit
                    if (!maxTags || (safeValue.length + newTags.length) <= maxTags) {
                        onChange([...safeValue, ...newTags]);
                        setInputValue('');
                    } else {
                        // Add only up to the limit
                        const availableSlots = maxTags - safeValue.length;
                        const tagsToAdd = newTags.slice(0, availableSlots);
                        onChange([...safeValue, ...tagsToAdd]);
                        setInputValue('');
                    }
                } else {
                    // Single tag entry
                    addTag(inputValue);
                }
            }
            setShowSuggestions(false);
        }
    };

    const handleRemoveTag = (indexToRemove: number) => {
        onChange(safeValue.filter((_, index) => index !== indexToRemove));
    };

    const handleInputChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setInputValue(newValue || '');
        // Show suggestions when user types and there are suggestions available
        if (suggestions.length > 0) {
            setShowSuggestions(true);
        }
    };
    
    const handleInputFocus = () => {
        if (suggestions.length > 0 && filteredSuggestions.length > 0) {
            setShowSuggestions(true);
        }
    };
    
    const handleInputBlur = () => {
        // Delay hiding to allow click on suggestion
        setTimeout(() => setShowSuggestions(false), 200);
    };
    
    const handleSuggestionClick = (suggestion: string) => {
        addTag(suggestion);
    };

    return (
        <Stack tokens={stackTokens}>
            <div className={styles.inputContainer} ref={inputRef}>
                <TextField
                    label={label}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder={placeholder}
                    description={description}
                    disabled={disabled}
                />
                
                {/* Suggestions Callout */}
                {showSuggestions && filteredSuggestions.length > 0 && inputRef.current && (
                    <Callout
                        target={inputRef.current}
                        isBeakVisible={false}
                        directionalHint={DirectionalHint.bottomLeftEdge}
                        gapSpace={2}
                        onDismiss={() => setShowSuggestions(false)}
                        styles={{ root: { minWidth: '200px', maxWidth: '400px' } }}
                    >
                        <div className={styles.suggestionsList}>
                            {filteredSuggestions.map((suggestion, idx) => (
                                <div
                                    key={suggestion}
                                    className={`${styles.suggestionItem} ${idx === selectedSuggestionIndex ? styles.suggestionItemSelected : ''}`}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSuggestionClick(suggestion);
                                    }}
                                >
                                    {suggestion}
                                </div>
                            ))}
                        </div>
                    </Callout>
                )}
            </div>
            
            {safeValue.length > 0 && (
                <div className={styles.tagContainer}>
                    {safeValue.map((tag, index) => (
                        <div key={index} className={styles.tag}>
                            <span className={styles.tagText} title={tag}>
                                {tag}
                            </span>
                            {!disabled && (
                                <IconButton
                                    className={styles.deleteButton}
                                    iconProps={{ iconName: 'Cancel' }}
                                    onClick={() => handleRemoveTag(index)}
                                    ariaLabel={`Remove ${tag}`}
                                    title={`Remove ${tag}`}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
            
            {maxTags && (
                <Text variant="small" style={{ color: '#605e5c', fontStyle: 'italic' }}>
                    {safeValue.length}/{maxTags} tags {maxTags - safeValue.length === 0 ? '(limit reached)' : ''}
                </Text>
            )}
        </Stack>
    );
};

export default TagInput;